import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { PostSection } from 'src/features/feeds/components/feeds/post-section'
import { useUserByUsername } from 'src/shared/features/users/api/accounts/use-user-by-username'
import { useUserPrivacyPreferences } from 'src/shared/features/users/api/preferences/use-user-privacy-preferences'
import UserProfileCard from 'src/shared/features/users/components/cards/user-profile-card'
import UserFeedList from 'src/shared/features/users/components/public-profile/feed/user-feed-list'
import ProfileOverview from 'src/shared/features/users/components/public-profile/overview/profile-overview'
import PortfolioList from 'src/shared/features/users/components/public-profile/portfolio/portfolio-list'
import ReviewList from 'src/shared/features/users/components/public-profile/reviews/review-list'
import PublicProfileReviewsSection from 'src/shared/features/users/components/public-profile/section/reviews'
import PublicProfileTransactionsSection from 'src/shared/features/users/components/public-profile/section/transactions'
import PublicProfileSectionHeader from 'src/shared/features/users/components/public-profile/section-header'
import { usePublicProfileStore } from 'src/shared/features/users/components/public-profile/stores/use-public-profile-store'
import PublicUserProfileFooter from 'src/shared/features/users/components/public-profile/ui/footer'
import { getCareerLength } from 'src/shared/features/users/components/view-profile/badges-section'
import { expertiseMap } from 'src/shared/features/users/schemas/expertise'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'

import ProfileTabBar from '../components/ui/profile-tab-bar'

function SectionDivider() {
  return <div className="h-px w-full bg-lightBorder" />
}

export const ProfileSectionsContainer = ({ userId, isPublic = false }: { userId: number; isPublic?: boolean }) => {
  const { activeTab, setIsPublicProfile } = usePublicProfileStore()
  const { data: preferences } = useUserPrivacyPreferences(userId)

  useEffect(() => {
    setIsPublicProfile(isPublic)
  }, [setIsPublicProfile, isPublic])

  const showActivity = preferences?.postsVisibleToConsumer

  return (
    <div className="flex w-full flex-col gap-4">
      <PublicProfileSectionHeader userId={userId} />
      {activeTab === 'activity' && showActivity && (
        <div className="-mt-8 w-full text-clip">
          <PostSection userId={userId} isPublic={isPublic} />
        </div>
      )}
      {activeTab === 'reviews' && (
        <div className="w-full text-clip">
          <PublicProfileReviewsSection userId={userId} />
        </div>
      )}
      {activeTab === 'transactions' && (
        <div className="w-full text-clip">
          <PublicProfileTransactionsSection userId={userId} />
        </div>
      )}
    </div>
  )
}

const PublicUserProfileView = ({ username }: { username: string }) => {
  const { data: user } = useUserByUsername(username)
  const { isMobile, isSmTabletOrSmaller } = useBreakpoint()
  const navigate = useNavigate()

  const { reset } = usePublicProfileStore()

  useEffect(() => {
    return () => {
      reset()
    }
  }, [reset])

  const shouldShowStartJourneyButton = user?.firstConversationWorkflowAvailable ?? false
  const [startJourneyButtonInView, setStartJourneyBtnInView] = useState(false)
  const portfolioRef = useRef<HTMLDivElement>(null)
  const reviewsRef = useRef<HTMLDivElement>(null)
  const activityRef = useRef<HTMLDivElement>(null)
  const overviewRef = useRef<HTMLDivElement>(null)

  const tabScrollOffset = isMobile ? 91 : isSmTabletOrSmaller ? 95 : 110

  const title = useMemo(() => {
    if (!user) {
      return ''
    }
    const address = user.shortAddress
    const profession = professionMap[user.profession as Profession]?.label
    let resultTitle = `${user.firstName} ${user.lastName} - ${profession}`
    if (address) {
      resultTitle += ` in ${address}`
    }
    return resultTitle
  }, [user])

  const description = useMemo(() => {
    const expertiseText = user?.expertise?.length
      ? `specializing in ${user.expertise
          .map((expertise) => expertiseMap[user?.profession]?.[expertise]?.label)
          .join(', ')}`
      : ''
    const careerText = user?.careerStartDate ? `working for over ${getCareerLength(user.careerStartDate)}` : ''

    return `${user?.fullName} is a ${
      professionMap[user?.profession as Profession]?.label
    } ${expertiseText} ${careerText} in ${user?.shortAddress}`
      .replace(/\s+/g, ' ')
      .trim()
  }, [user])

  if (!user) {
    navigate('/404')
    return null
  }

  const startJourneyButtonInViewOnChange = (inView: boolean) => {
    setStartJourneyBtnInView(inView)
  }

  const showFooter = !startJourneyButtonInView

  return (
    <>
      {title && <title>{title}</title>}
      <meta name="description" content={description} />
      <meta property="og:description" content={description} />
      <meta name="twitter:description" content={description} />
      <meta property="og:image" content={user?.profilePhoto} />
      <meta name="twitter:image" content={user?.profilePhoto} />
      <meta name="theme-color" content="#f8f7f5" />
      <div className="mx-auto max-w-[1400px]">
        <div className="mx-auto flex max-w-[1100px] flex-col pb-[180px]">
          {/* User Profile Card */}
          <div className="relative z-20 mb-[20px]">
            <UserProfileCard
              user={user}
              isPublic={true}
              startJourneyButtonInViewOnChange={startJourneyButtonInViewOnChange}
              buttons={shouldShowStartJourneyButton ? 'start-journey' : 'none'}
            />
          </div>
          <ProfileTabBar
            sections={[
              { id: 'overview', label: 'Overview', ref: overviewRef },
              { id: 'portfolio', label: 'Portfolio', ref: portfolioRef },
              { id: 'reviews', label: 'Reviews', ref: reviewsRef },
              { id: 'activity', label: 'Activity', ref: activityRef },
            ]}
            tabScrollOffset={tabScrollOffset}
            defaultActiveTab="overview"
            sticky={true}
          />

          <div className="mx-auto mt-[30px] w-full max-w-[1000px] space-y-[40px] px-5 md:space-y-[60px] md:px-0">
            {/* Overview */}
            <div ref={overviewRef}>
              <ProfileOverview userId={user.id} />
            </div>
            <SectionDivider />
            {/* Portfolio */}
            <div ref={portfolioRef}>
              <PortfolioList
                userId={user.id}
                username={username as string}
                profession={user.profession}
                firstName={user.firstName}
              />
            </div>
            <SectionDivider />
            {/* Reviews */}
            <div ref={reviewsRef}>
              <ReviewList firstName={user.firstName} userId={user.id} username={username as string} />
            </div>
            <SectionDivider />
            {/* Activity */}
            <div ref={activityRef} className="min-h-[calc(100vh-230px)] md:min-h-[calc(100vh-260px)]">
              <UserFeedList userId={user.id} firstName={user.firstName} />
            </div>
          </div>
        </div>
        {/* Footer */}
        <AnimatePresence>
          {showFooter && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <PublicUserProfileFooter username={user.username} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}

export default PublicUserProfileView
